import React from 'react';
import { GlobeIcon, SparklesIcon, UserIcon, MailIcon, MessagesSquareIcon, PhoneIcon, MessageCircleIcon, MoreHorizontalIcon } from 'lucide-react';
import { useLanguage } from '../../LanguageContext';
import { WebsiteLaptop, PortalScreen, AppPhone } from '../DigitalDevices';
import { FitScale } from '../FitScale';
import { dxExperiences, dxChannels, type Bi, type ExperienceKey, type ChannelKey } from '../../../data/digitalExperienceSolution';
export const EXPERIENCE_ICONS: Record<ExperienceKey, typeof GlobeIcon> = {
  web: GlobeIcon,
  app: SparklesIcon,
  portal: UserIcon
};
export const CHANNEL_ICONS: Record<ChannelKey, typeof GlobeIcon> = {
  email: MailIcon,
  social: MessagesSquareIcon,
  call: PhoneIcon,
  llm: SparklesIcon,
  forms: MessageCircleIcon,
  other: MoreHorizontalIcon
};
const PORTAL_STATS = [{
  value: '3',
  label: {
    fr: 'Demandes en cours',
    en: 'Open requests'
  }
}, {
  value: '2',
  label: {
    fr: 'Notifications',
    en: 'Notifications'
  }
}];
function usePick() {
  const {
    lang
  } = useLanguage();
  return (b: Bi) => lang === 'FR' ? b.fr : b.en;
}
function Scaled({
  scale,
  w,
  h,
  children
}: {
  scale: number;
  w: number;
  h: number;
  children: React.ReactNode;
}) {
  return <div style={{
    width: w * scale,
    height: h * scale
  }} data-id="element-3400" data-mp="3qqheci19p76k">
      <div className="origin-top-left" style={{
      width: w,
      transform: `scale(${scale})`
    }} data-id="element-3401" data-mp="3qpy7pxh2i57u">{children}</div>
    </div>;
}
function ExperienceLabel({
  k
}: {
  k: ExperienceKey;
}) {
  const pick = usePick();
  const item = dxExperiences.items.find(i => i.key === k)!;
  const Icon = EXPERIENCE_ICONS[k];
  return <div className="flex flex-col items-center text-center" data-id="element-3402" data-mp="2t0w7dzwav5mr">
      <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-3403" data-mp="37swz82ob1mz5" />
      <p className="mt-2 font-sans text-[17px] font-semibold text-[#121A3A] whitespace-nowrap" data-id="element-3404" data-mp="330vfxwspyvzm">{pick(item.label)}</p>
      <p className="font-sans text-[13px] leading-snug text-[#5B6478] mt-0.5 max-w-[190px]" data-id="element-3405" data-mp="2faj265asm514">{pick(item.text)}</p>
    </div>;
}
export function ExperienceDevice({
  k
}: {
  k: ExperienceKey;
}) {
  if (k === 'web') return <Scaled scale={0.55} w={580} h={414} data-id="element-3406" data-mp="e8tj3emhyde9"><WebsiteLaptop data-id="element-3407" data-mp="j8fy9tlvy3pp" /></Scaled>;
  if (k === 'app') return <Scaled scale={0.6} w={196} h={424} data-id="element-3408" data-mp="j74sxpcsknwh"><AppPhone data-id="element-3409" data-mp="j6366nmeycyx" /></Scaled>;
  return <Scaled scale={0.55} w={440} h={416} data-id="element-3410" data-mp="nymjrkl3p83q"><PortalScreen stats={PORTAL_STATS} data-id="element-3411" data-mp="o0fubayu4ch0" /></Scaled>;
}
function SectionHeading({
  heading,
  sub
}: {
  heading: Bi;
  sub: Bi;
}) {
  const pick = usePick();
  return <div className="text-center" data-id="element-3412" data-mp="3rg4srvcm0l5q">
      <p className="font-sans text-[18px] font-semibold uppercase tracking-wide text-[#4F46E5]" data-id="element-3413" data-mp="3rh6ceo7oyfz2">{pick(heading)}</p>
      <p className="font-sans text-[15px] text-[#4A5268] mt-0.5" data-id="element-3414" data-mp="9tw0bemu6k7">{pick(sub)}</p>
    </div>;
}
export function ChannelCard({
  k
}: {
  k: ChannelKey;
}) {
  const pick = usePick();
  const item = dxChannels.items.find(i => i.key === k)!;
  const Icon = CHANNEL_ICONS[k];
  return <div className="h-full rounded-2xl bg-white border border-[#EEF0F4] px-3 py-5 flex flex-col items-center text-center" data-id="element-3415" data-mp="1fpiw8k391aqh">
      <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-3416" data-mp="1knkgh8cevg3j" />
      <p className="mt-3 font-sans text-[14px] font-semibold text-[#121A3A] leading-tight" data-id="element-3417" data-mp="1usu4n74hzurl">{pick(item.label)}</p>
      <p className="mt-2 font-sans text-[11.5px] leading-snug text-[#6B7180]" data-id="element-3418" data-mp="1pv0ww9njqsp7">{pick(item.text)}</p>
    </div>;
}

// Desktop / tablet canvas: our three experiences (primary) + other channels (secondary).
export function SolutionTopCanvas() {
  return <div className="relative w-[1240px] h-[470px]" data-id="element-3419" data-mp="1c34b0nch1f19">
      <div className="absolute left-0 top-0 w-[770px] h-[470px] rounded-[30px] bg-[#F6F6FD] border border-[#EEEDFB] pt-6" data-id="element-3420" data-mp="1c0rk3koqjuc2">
        <SectionHeading heading={dxExperiences.heading} sub={dxExperiences.sub} data-id="element-3421" data-mp="124eqsnx3rihu" />
        <div className="absolute left-[22px] top-[88px] w-[326px] h-[360px] rounded-2xl bg-white/80 pt-4 flex flex-col items-center" data-id="element-3422" data-mp="126hqjzo24pzr">
          <ExperienceLabel k="web" data-id="element-3423" data-mp="2wj7hq9n1jl3g" />
          <div className="mt-4" data-id="element-3424" data-mp="2wi5yvizs9ub6"><ExperienceDevice k="web" data-id="element-3425" data-mp="2wglayc7z59ma" /></div>
        </div>
        <div className="absolute left-[358px] top-[88px] w-[160px] h-[360px] rounded-2xl bg-white/80 pt-4 flex flex-col items-center" data-id="element-3426" data-mp="2si5ktzgljl4n">
          <ExperienceLabel k="app" data-id="element-3427" data-mp="2zi7owy0ktp8m" />
          <div className="mt-3" data-id="element-3428" data-mp="34jx4mqyqur31"><ExperienceDevice k="app" data-id="element-3429" data-mp="34icah2gcda5p" /></div>
        </div>
        <div className="absolute left-[528px] top-[88px] w-[222px] h-[360px] rounded-2xl bg-white/80 pt-4 flex flex-col items-center" data-id="element-3430" data-mp="2qjmclklp76ga">
          <ExperienceLabel k="portal" data-id="element-3431" data-mp="2ukfx03zxm34f" />
          <div className="mt-4 -mx-4" data-id="element-3432" data-mp="2unak63aby21j"><ExperienceDevice k="portal" data-id="element-3433" data-mp="2gtsxi9mck97a" /></div>
        </div>
      </div>

      <div className="absolute left-[790px] top-0 w-[450px] h-[400px] rounded-[30px] bg-[#F8F8FC] border border-[#F0F0F6] pt-6 px-4" data-id="element-3434" data-mp="2grq0v6qovhtl">
        <SectionHeading heading={dxChannels.heading} sub={dxChannels.sub} data-id="element-3435" data-mp="26vwrtxc6i6gk" />
        <div className="mt-5 grid grid-cols-3 grid-rows-2 gap-2.5 h-[300px]" data-id="element-3436" data-mp="26tseypwaxx45">
          {dxChannels.items.map(c => <ChannelCard key={c.key} k={c.key} data-id="element-3437" data-mp="1xuykp66g1itr" />)}
        </div>
      </div>
    </div>;
}

// Mobile: experiences stacked, each with its own mockup.
export function ExperiencesMobile() {
  return <div className="rounded-[26px] bg-[#F6F6FD] border border-[#EEEDFB] p-5" data-id="element-3438" data-mp="1j889nd2x4buz">
      <SectionHeading heading={dxExperiences.heading} sub={dxExperiences.sub} data-id="element-3439" data-mp="1jacb7odcgys3" />
      <div className="mt-5 space-y-4" data-id="element-3440" data-mp="1eapydg1fhkjt">
        {dxExperiences.items.map(e => <div key={e.key} className="rounded-2xl bg-white p-4 flex flex-col items-center" data-id="element-3441" data-mp="1s9zbu7y920k3">
            <ExperienceLabel k={e.key} data-id="element-3442" data-mp="1o69rxvsis75e" />
            <div className="mt-4 w-full flex justify-center" data-id="element-3443" data-mp="1o9fh1b15f914">
              {e.key === 'app' ? <ExperienceDevice k="app" data-id="element-3444" data-mp="225j9aysgp7ay" /> : <FitScale width={e.key === 'web' ? 320 : 242} height={e.key === 'web' ? 228 : 229} data-id="element-3445" data-mp="1x8amgkwdcf3d"><ExperienceDevice k={e.key} data-id="element-3446" data-mp="1x66jq6a7hm4o" /></FitScale>}
            </div>
          </div>)}
      </div>
    </div>;
}
export function ChannelsMobile() {
  return <div className="rounded-[26px] bg-[#F8F8FC] border border-[#F0F0F6] p-5" data-id="element-3447" data-mp="26zx6n6qopoi9">
      <SectionHeading heading={dxChannels.heading} sub={dxChannels.sub} data-id="element-3448" data-mp="2ky4xqy11hvli" />
      <div className="mt-5 grid grid-cols-2 gap-2.5" data-id="element-3449" data-mp="2fzjyb0tm3fy4">
        {dxChannels.items.map(c => <ChannelCard key={c.key} k={c.key} data-id="element-3450" data-mp="1dlmdvppq61ld" />)}
      </div>
    </div>;
}